"use client";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { luxeFloat, LUXE_ACCENT } from "../luxe-shared/keyframes";

export interface PromoCountdownSlot {
  value: string;
  unit: string;
}

export interface PromoBannerLuxeProps {
  sectionId?: string;
  badge?: string;
  headline?: string;
  headlineAccent?: string;
  description?: string;
  countdown?: PromoCountdownSlot[];
  image?: { src: string; alt: string };
}

const PromoBannerLuxe: React.FC<PromoBannerLuxeProps> = ({
  sectionId = "deals",
  badge = "NEON FLASH SALE",
  headline = "UP TO",
  headlineAccent = "40% OFF",
  description = "",
  countdown = [],
  image,
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  return (
    <Box
      id={sectionId}
      component="section"
      sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, md: 3 }, py: 6 }}
    >
      <Box
        sx={{
          position: "relative",
          borderRadius: "3rem",
          overflow: "hidden",
          p: { xs: 4, md: 8 },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          gap: 6,
          border: `1px solid ${primary}33`,
          background: `linear-gradient(to bottom right, #fff, ${primary}0d)`,
          bgcolor: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(16px)",
          WebkitBackdropFilter: "blur(16px)",
          boxShadow: "0 10px 30px -10px rgba(0,0,0,0.05)",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 384,
            height: 384,
            bgcolor: `${primary}1a`,
            filter: "blur(100px)",
            zIndex: 0,
            animation: "pulse 3s ease-in-out infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 0.6 },
              "50%": { opacity: 1 },
            },
          }}
        />

        <Stack
          spacing={3}
          sx={{
            position: "relative",
            zIndex: 1,
            flex: 1,
            textAlign: { xs: "center", md: "left" },
            alignItems: { xs: "center", md: "flex-start" },
          }}
        >
          <Box
            sx={{
              display: "inline-block",
              px: 2,
              py: 0.75,
              borderRadius: 2,
              bgcolor: LUXE_ACCENT,
              color: "#fff",
              fontWeight: 800,
              fontSize: "0.75rem",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
              animation: "pulse 2s ease-in-out infinite",
            }}
          >
            {badge}
          </Box>

          <Typography
            component="h2"
            sx={{
              fontWeight: 900,
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              fontSize: { xs: "2.5rem", md: "4.5rem" },
              color: "text.primary",
            }}
          >
            {headline}{" "}
            <Box component="span" sx={{ color: primary }}>
              {headlineAccent}
            </Box>
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1.25rem",
              fontWeight: 500,
              maxWidth: 480,
            }}
          >
            {description}
          </Typography>

          <Stack direction="row" spacing={3} justifyContent={{ xs: "center", md: "flex-start" }}>
            {countdown.map((slot) => (
              <Box
                key={slot.unit}
                sx={{
                  borderRadius: 2,
                  p: 2.5,
                  minWidth: 100,
                  border: `1px solid ${primary}4d`,
                  bgcolor: "rgba(255,255,255,0.8)",
                  backdropFilter: "blur(16px)",
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{ fontSize: "2.25rem", fontWeight: 900, color: primary, lineHeight: 1 }}
                >
                  {slot.value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "10px",
                    color: "text.secondary",
                    fontWeight: 800,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    mt: 0.5,
                  }}
                >
                  {slot.unit}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>

        {image ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: { xs: "100%", md: "33%" },
              animation: `${luxeFloat} 6s ease-in-out infinite`,
              animationDuration: "4s",
            }}
          >
            <Box
              component="img"
              src={image.src}
              alt={image.alt}
              sx={{
                width: "100%",
                height: "auto",
                transform: "rotate(6deg) scale(1.1)",
                mixBlendMode: "multiply",
                filter: "contrast(1.1)",
              }}
            />
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};

export default PromoBannerLuxe;
